import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AdminMetaAdCatalogDto,
    AdminMetaAdProductCardDto,
    AdminMetaAdSlotDto,
    getAdminMetaAdCatalog,
    getAdminMetaAdSuggestions,
    saveAdminMetaAdCatalog,
    searchAdminMetaAdProducts,
} from '../../services/api.admin';
import { Package, RefreshCw, Search, Sparkles, Trash2, X } from 'lucide-react';

const formatPrice = (price: number) =>
    new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(price);

const emptySlots = (count: number): AdminMetaAdSlotDto[] =>
    Array.from({ length: count }, (_, i) => ({
        slotIndex: i,
        productId: null,
        product: null,
        isInvalid: false,
    }));

const MetaAdCatalogPanel = () => {
    const [catalog, setCatalog] = useState<AdminMetaAdCatalogDto | null>(null);
    const [slots, setSlots] = useState<AdminMetaAdSlotDto[]>([]);
    const [slotCount, setSlotCount] = useState(12);
    const [activeSlot, setActiveSlot] = useState<number | null>(null);
    const [filterQ, setFilterQ] = useState('');
    const [searchItems, setSearchItems] = useState<AdminMetaAdProductCardDto[]>([]);
    const [searchTotal, setSearchTotal] = useState(0);
    const [searchPage, setSearchPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);

    const selectedIds = useMemo(
        () => new Set(slots.map((s) => s.productId).filter((id): id is number => id != null)),
        [slots]
    );

    const filledCount = selectedIds.size;

    const applyCatalog = (data: AdminMetaAdCatalogDto) => {
        setCatalog(data);
        setSlotCount(data.slotCount);
        const mapped =
            data.slots?.length >= data.slotCount
                ? data.slots.slice(0, data.slotCount)
                : [
                      ...data.slots,
                      ...emptySlots(data.slotCount).slice(data.slots?.length ?? 0),
                  ];
        setSlots(
            mapped.map((s, i) => ({
                ...s,
                slotIndex: i,
            }))
        );
        setDirty(false);
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAdminMetaAdCatalog();
            applyCatalog(data);
        } catch (e) {
            console.error(e);
            setError('Failed to load Meta ad catalog.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load().catch(() => undefined);
    }, [load]);

    const runSearch = useCallback(async (q: string, page: number) => {
        setSearching(true);
        setError(null);
        try {
            const res = await searchAdminMetaAdProducts({ q: q || undefined, page, limit: 24 });
            setSearchItems(res.items);
            setSearchTotal(res.total);
            setSearchPage(res.page);
        } catch (e) {
            console.error(e);
            setError('Product search failed.');
        } finally {
            setSearching(false);
        }
    }, []);

    // Debounced filter when picker is open
    useEffect(() => {
        if (activeSlot === null) return;
        const t = window.setTimeout(() => {
            runSearch(filterQ.trim(), 1).catch(() => undefined);
        }, 300);
        return () => window.clearTimeout(t);
    }, [filterQ, activeSlot, runSearch]);

    const resizeSlots = (nextCount: number) => {
        const n = Math.min(50, Math.max(1, nextCount || 1));
        setSlotCount(n);
        setSlots((prev) => {
            if (n === prev.length) return prev;
            if (n < prev.length) return prev.slice(0, n).map((s, i) => ({ ...s, slotIndex: i }));
            return [
                ...prev,
                ...emptySlots(n - prev.length).map((s, i) => ({
                    ...s,
                    slotIndex: prev.length + i,
                })),
            ];
        });
        setDirty(true);
        setMessage(null);
    };

    const clearSlot = (index: number) => {
        setSlots((prev) =>
            prev.map((s) =>
                s.slotIndex === index
                    ? { slotIndex: index, productId: null, product: null, isInvalid: false }
                    : s
            )
        );
        setDirty(true);
        setMessage(null);
    };

    const assignProduct = (index: number, product: AdminMetaAdProductCardDto) => {
        setSlots((prev) => {
            // If product already in another slot, swap/clear that slot
            const existingIndex = prev.findIndex((s) => s.productId === product.id);
            let next = prev.map((s) => ({ ...s }));
            if (existingIndex >= 0 && existingIndex !== index) {
                next[existingIndex] = {
                    slotIndex: existingIndex,
                    productId: null,
                    product: null,
                    isInvalid: false,
                };
            }
            next[index] = {
                slotIndex: index,
                productId: product.id,
                product,
                isInvalid: product.isSold || product.status !== 'Active' || !product.imageUrl,
                invalidReason:
                    product.isSold || product.status === 'Sold'
                        ? 'Sold'
                        : product.status !== 'Active'
                          ? product.status
                          : !product.imageUrl
                            ? 'No image'
                            : undefined,
            };
            return next;
        });
        setDirty(true);
        setMessage(null);
        setActiveSlot(null);
    };

    const fillRecommendations = async (mode: 'empty' | 'all') => {
        setError(null);
        setMessage(null);
        try {
            const suggestions = await getAdminMetaAdSuggestions(slotCount);
            if (!suggestions.length) {
                setError('No recommended products available (need Active listings with photos).');
                return;
            }
            setSlots((prev) => {
                const used = new Set<number>();
                if (mode === 'empty') {
                    prev.forEach((s) => {
                        if (s.productId != null) used.add(s.productId);
                    });
                }
                let sugIdx = 0;
                return prev.map((s) => {
                    if (mode === 'empty' && s.productId != null) return s;
                    while (sugIdx < suggestions.length && used.has(suggestions[sugIdx].id)) {
                        sugIdx++;
                    }
                    if (sugIdx >= suggestions.length) {
                        return mode === 'all'
                            ? { slotIndex: s.slotIndex, productId: null, product: null, isInvalid: false }
                            : s;
                    }
                    const p = suggestions[sugIdx++];
                    used.add(p.id);
                    return {
                        slotIndex: s.slotIndex,
                        productId: p.id,
                        product: p,
                        isInvalid: !p.imageUrl,
                        invalidReason: !p.imageUrl ? 'No image' : undefined,
                    };
                });
            });
            setDirty(true);
            setMessage(
                mode === 'all'
                    ? 'Filled all slots from recommendations. Review and Save.'
                    : 'Filled empty slots from recommendations. Review and Save.'
            );
        } catch (e) {
            console.error(e);
            setError('Could not load recommendations.');
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            const saved = await saveAdminMetaAdCatalog({
                slotCount,
                slots: slots.map((s) => ({
                    slotIndex: s.slotIndex,
                    productId: s.productId,
                })),
            });
            applyCatalog(saved);
            setMessage('Catalog saved. Public Meta feed will use these products (when any slot is filled).');
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { data?: { errors?: { General?: string } } } }).response?.data
                          ?.errors?.General
                    : null;
            setError(msg || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const openPicker = (index: number) => {
        setActiveSlot(index);
        setFilterQ('');
        runSearch('', 1).catch(() => undefined);
    };

    if (loading && !catalog) {
        return (
            <div className="flex h-64 flex-col items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-t-4 border-b-4 border-indigo-600" />
                <p className="mt-4 text-gray-600">Loading Meta ad catalog…</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Meta ad catalog</h1>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600">
                        Fixed slots for the public Meta product feed. Fill with recommendations, then swap
                        any slot. Empty catalog falls back to automatic ranking.
                    </p>
                    {catalog?.updatedAt && (
                        <p className="mt-1 text-xs text-gray-400">
                            Last saved: {new Date(catalog.updatedAt).toLocaleString()}
                            {dirty ? ' · unsaved changes' : ''}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => fillRecommendations('empty')}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
                    >
                        <Sparkles size={16} />
                        Fill empty
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (
                                filledCount > 0 &&
                                !window.confirm('Replace all slots with fresh recommendations?')
                            ) {
                                return;
                            }
                            fillRecommendations('all');
                        }}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                    >
                        <RefreshCw size={16} />
                        Recommend all
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !dirty}
                        className="inline-flex min-h-11 items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Save catalog'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                </div>
            )}
            {message && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    {message}
                </div>
            )}

            <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
                    Slot count
                    <input
                        type="number"
                        min={1}
                        max={50}
                        value={slotCount}
                        onChange={(e) => resizeSlots(Number(e.target.value))}
                        className="min-h-11 w-24 rounded-lg border border-gray-300 px-3 py-2"
                    />
                </label>
                <p className="pb-2 text-sm text-gray-500">
                    {filledCount} / {slotCount} filled
                    {slots.some((s) => s.isInvalid) && (
                        <span className="ml-2 text-amber-700">· some slots invalid for feed</span>
                    )}
                </p>
                <a
                    href="/api/public/meta-catalog"
                    target="_blank"
                    rel="noreferrer"
                    className="pb-2 text-sm font-medium text-indigo-600 hover:underline"
                >
                    Preview public JSON →
                </a>
            </div>

            {/* Slot grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
                {slots.map((slot) => (
                    <div
                        key={slot.slotIndex}
                        className={`relative flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition ${
                            activeSlot === slot.slotIndex
                                ? 'border-indigo-500 ring-2 ring-indigo-200'
                                : slot.isInvalid
                                  ? 'border-amber-300'
                                  : slot.product
                                    ? 'border-gray-200'
                                    : 'border-dashed border-gray-300'
                        }`}
                    >
                        <div className="flex items-center justify-between border-b border-gray-100 px-2 py-1.5 text-xs font-semibold text-gray-500">
                            <span>Slot {slot.slotIndex + 1}</span>
                            {slot.product && (
                                <button
                                    type="button"
                                    onClick={() => clearSlot(slot.slotIndex)}
                                    className="inline-flex min-h-8 min-w-8 items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600"
                                    title="Clear slot"
                                    aria-label={`Clear slot ${slot.slotIndex + 1}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => openPicker(slot.slotIndex)}
                            className="flex min-h-[140px] flex-1 flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                        >
                            {slot.product ? (
                                <>
                                    <div className="aspect-square w-full bg-gray-100">
                                        {slot.product.imageUrl ? (
                                            <img
                                                src={slot.product.imageUrl}
                                                alt=""
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full items-center justify-center text-gray-400">
                                                <Package size={28} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-1 flex-col gap-0.5 p-2">
                                        <p className="line-clamp-2 text-xs font-semibold text-gray-900">
                                            {slot.product.title}
                                        </p>
                                        <p className="text-xs font-medium text-indigo-700">
                                            {formatPrice(slot.product.price)}
                                        </p>
                                        <p className="text-[10px] text-gray-400">#{slot.product.id}</p>
                                        {slot.isInvalid && (
                                            <span className="mt-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                                {slot.invalidReason || 'Invalid'}
                                            </span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center text-gray-400">
                                    <Package size={28} />
                                    <span className="text-xs font-medium">Click to pick product</span>
                                </div>
                            )}
                        </button>
                    </div>
                ))}
            </div>

            {/* Picker drawer / panel */}
            {activeSlot !== null && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
                    <div
                        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
                        role="dialog"
                        aria-modal
                        aria-labelledby="meta-picker-title"
                    >
                        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-6">
                            <div>
                                <h2 id="meta-picker-title" className="text-lg font-bold text-gray-900">
                                    Pick product for slot {activeSlot + 1}
                                </h2>
                                <p className="text-xs text-gray-500">
                                    Filter by title, id, seller or category. Already selected products are
                                    marked.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setActiveSlot(null)}
                                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
                                aria-label="Close picker"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="border-b border-gray-100 px-4 py-3 sm:px-6">
                            <label className="relative block">
                                <Search
                                    size={16}
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="search"
                                    value={filterQ}
                                    onChange={(e) => setFilterQ(e.target.value)}
                                    placeholder="Quick filter: title, #id, seller, category…"
                                    className="min-h-11 w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
                                    autoFocus
                                />
                            </label>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6">
                            {searching && (
                                <p className="mb-3 text-sm text-gray-500">Searching…</p>
                            )}
                            {!searching && searchItems.length === 0 && (
                                <p className="py-8 text-center text-sm text-gray-500">
                                    No active products match this filter.
                                </p>
                            )}
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                {searchItems.map((p) => {
                                    const already = selectedIds.has(p.id);
                                    const inThisSlot = slots[activeSlot]?.productId === p.id;
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => assignProduct(activeSlot, p)}
                                            className={`overflow-hidden rounded-xl border text-left transition hover:border-indigo-400 hover:shadow ${
                                                inThisSlot
                                                    ? 'border-indigo-500 ring-2 ring-indigo-200'
                                                    : already
                                                      ? 'border-amber-200 bg-amber-50/40'
                                                      : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            <div className="aspect-square bg-gray-100">
                                                {p.imageUrl ? (
                                                    <img
                                                        src={p.imageUrl}
                                                        alt=""
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full items-center justify-center text-gray-300">
                                                        <Package size={24} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-0.5 p-2">
                                                <p className="line-clamp-2 text-xs font-semibold text-gray-900">
                                                    {p.title}
                                                </p>
                                                <p className="text-xs text-indigo-700">{formatPrice(p.price)}</p>
                                                <p className="text-[10px] text-gray-400">
                                                    #{p.id}
                                                    {p.sellerName ? ` · ${p.sellerName}` : ''}
                                                </p>
                                                {already && !inThisSlot && (
                                                    <span className="inline-block text-[10px] font-medium text-amber-700">
                                                        In another slot (will move)
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {searchTotal > searchItems.length && (
                                <div className="mt-4 flex justify-center gap-2">
                                    <button
                                        type="button"
                                        disabled={searchPage <= 1 || searching}
                                        onClick={() => runSearch(filterQ.trim(), searchPage - 1)}
                                        className="min-h-11 rounded-lg border px-3 text-sm disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="flex items-center text-xs text-gray-500">
                                        Page {searchPage} · {searchTotal} total
                                    </span>
                                    <button
                                        type="button"
                                        disabled={
                                            searching || searchPage * 24 >= searchTotal
                                        }
                                        onClick={() => runSearch(filterQ.trim(), searchPage + 1)}
                                        className="min-h-11 rounded-lg border px-3 text-sm disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetaAdCatalogPanel;
